import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {DocumentClass} from "../../../../model/documentClass";
import {User} from "../../../../model/user";
import {Document} from "../../../../model/document";
import {Product} from "../../../../model/product";
import {Property} from "../../../../model/property";
import {ProductService} from "../../../../service/product.service";
import {SubClass} from "../../../../model/subClass";
import {ModalComponent} from "../../../modal/modal.component";
import {PricingUtils} from "../../../../utils/pricing-utils";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationDialogComponent} from "../../../dialogs/confirmation-dialog/confirmation-dialog.component";
import {SubInformation} from "../../../../model/subInformation";
import {AdditionalCertificateData} from "../../../../model/additionalCertificateData";
import {CertificateData} from "../../../../model/certificateData";
import {ObjectOwnerComponent} from "../../../object-owner/object-owner.component";
import {FolioNumberComponent} from "../../../folio-number/folio-number.component";
import {ObjectOwner} from "../../../../model/object-owner";
import {LandregisterfolioUtils} from "../../../../utils/LandregisterfolioUtils";
import FormUtils from "../../../../utils/form-utils";
import {Subscription} from "rxjs";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: 'app-grundakte-modal',
  templateUrl: './grundakte-modal.component.html',
  styleUrls: ['./grundakte-modal.component.css']
})
export class GrundakteModalComponent implements OnInit, OnChanges, OnDestroy {

  @Input() active: boolean = false;
  @Input() user: User;
  @Input() documents: Document[] = [];
  @Input() grundakteProducts: Product[] = [];
  @Input() isAllowedToOrder: boolean = false;
  @Input() products: Product[] = [];
  @Input() selectedProperty: Property;
  @Input() isConnectOrigin: boolean = false;

  @Output() modalClosed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<Document[]>();
  @Output() removeRequested = new EventEmitter<void>();

  @ViewChild('modalRef') modalRef: ModalComponent;
  @ViewChild(ObjectOwnerComponent) objectOwnerComponent: ObjectOwnerComponent;
  @ViewChild(FolioNumberComponent) folioNumberComponent: FolioNumberComponent;

  grundakteForm: UntypedFormGroup;
  certificateForm: UntypedFormGroup;
  readonly bewiluKey: string = 'BEWILU';

  bewilDocumentClass: DocumentClass;
  bewilSubClasses: SubClass[] = [];
  private lfdSubClassKey?: string;
  private bewilShortName?: string;
  private teilungShortName?: string;

  fixedBewilPrice: number;
  bewilCertificatePrice: number;
  teilungPrice: number;

  step: 1 | 2 = 1;
  showInvalidInputs: boolean = false;
  private pendingDocuments: Document[] = [];
  private subClassSubscriptions: Subscription[] = [];

  protected readonly PricingUtils = PricingUtils;
  private readonly certificateFieldKeys = ['landRegisterPage', 'department', 'serialNumber', 'certificate'];
  protected readonly faInfoCircle = faInfoCircle;

  constructor(private readonly formBuilder: UntypedFormBuilder,
              private readonly productService: ProductService,
              private readonly dialog: MatDialog) {}

  ngOnInit(): void {
    this.grundakteForm = this.formBuilder.group({
      grundakteSelections: this.formBuilder.group({}),
      bewilSubclasses: this.formBuilder.group({})
    });

    this.certificateForm = this.formBuilder.group({
      lfdCertificates: this.formBuilder.array([this.createCertificateGroup()])
    });

    this.fixedBewilPrice = PricingUtils.getFixedBewilPrice();
    this.bewilCertificatePrice = PricingUtils.getBewilCertificatePrice();
    this.teilungPrice = PricingUtils.getTeilunPricing();

    this.initSelectionControls();
    this.initBewilDocumentClass();
  }

  ngOnDestroy(): void {
    this.clearSubClassSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['grundakteProducts'] && this.grundakteForm) {
      this.initSelectionControls();
    }

    if (changes['active']) {
      const isOpening = changes['active'].currentValue === true && changes['active'].previousValue !== true;
      const isClosing = changes['active'].currentValue === false && changes['active'].previousValue === true;
      if (isOpening) {
        this.step = 1;
        this.showInvalidInputs = false;
        this.updateValuesForExistingGrundakteDocuments();
      }
      if (isClosing) {
        this.resetPendingState();
      }
    }

    if ((changes['documents'] || changes['active']) && this.active) {
      this.updateValuesForExistingGrundakteDocuments();
    }
  }

  /**
   * Determines if the "GrundeStep" condition is fulfilled based on several criteria.
   * @return {boolean} True if the "GrundeStep" condition is fulfilled, otherwise false.
   */
  public get hasGrundeStep(): boolean {
    return !!this.grundeShortName
      && !this.hasExistingGrunde
      && !this.isConnectOrigin
      && this.shouldAddGrundeForSelection();
  }

  /**
   * Sorts the available 'grundakteProducts' list by their priority.
   * @return {Product[]} Sorted list of available 'grundakteProducts'.
   */
  public sortedGrundakteProducts(): Product[] {
    return [...(this.grundakteProducts || [])]
      .sort((a, b) => this.getGrundaktePriority(a) - this.getGrundaktePriority(b));
  }

  /**
   * Determines if 'this.documents' contain any document which is part of the 'Grundakte' category
   * @return {boolean} True if 'this.documents' contain any document which is part of the 'Grundakte' category, otherwise false.
   */
  public hasGrundakteDocument(): boolean {
    const shortNames = (this.grundakteProducts || []).map(product => (product?.nameShort || '').toLowerCase());
    return (this.documents || []).some(doc => shortNames.includes((doc?.docname || '').toLowerCase()));
  }

  /**
   * Determines if the provided short-name is associated with a 'bewil' product.
   * @param shortName Short name of a document to be checked
   * @return {boolean} True if the provided short-name is associated with a 'bewil' product, otherwise false.
   */
  public isBewilligungsurkunde(shortName: string): boolean {
    return this.normalize(shortName).includes('bewil');
  }

  /**
   * Determines if the provided short-name is associated with a 'teilung' product.
   * @param shortName Short name of a document to be checked
   * @return {boolean} True if the provided short-name is associated with a 'teilung' product, otherwise false.
   */
  public isTeilungserklaerung(shortName: string): boolean {
    return this.normalize(shortName).includes('teil');
  }

  /**
   * Determines if a specific subclass for 'Bewilligungsurkunden zu ausgewählten Rechten' is selected within the `bewilSubclasses` form group.
   *
   * @return {boolean} True if the subclass is selected, otherwise false.
   */
  public isLfdSubClassSelected(): boolean {
    if (!this.lfdSubClassKey) {
      return false;
    }
    const group = this.grundakteForm.get('bewilSubclasses') as UntypedFormGroup;
    return !!group?.get(this.lfdSubClassKey)?.value;
  }

  /**
   * Helper which determines with specific inputs for 'Bewilligungsurkunden zu ausgewählten Rechten' should be displayed.
   *
   * @return {boolean} True if the inputs should be displayed, otherwise false.
   */
  public shouldDisplayLfdCertificates(): boolean {
    return this.isLfdSubClassSelected();
  }

  /**
   * Determines whether the product selection is invalid based on specific conditions.
   * The selection is considered invalid if invalid inputs are displayed
   * and no products have been selected.
   *
   * @return {boolean} True if the product selection is invalid, false otherwise.
   */
  public get isProductSelectionInvalid(): boolean {
    return this.showInvalidInputs && this.getSelectedProductKeys().length === 0;
  }

  /**
   * Checks if there is a selection error for Bewil by validating whether a Bewil is selected
   * and ensuring that the selected subclasses array is not empty.
   *
   * @return {boolean} Returns true if there is a Bewil selection error, otherwise false.
   */
  public hasBewilSelectionError(): boolean {
    return this.hasBewilSelected() && this.selectedBewilSubclasses().length === 0;
  }

  /**
   * Checks whether there is a certificate error based on subclass selection and validation errors.
   *
   * @return {boolean} Returns true if there is a certificate error, false otherwise.
   */
  public hasCertificateError(): boolean {
    return this.isLfdSubClassSelected() && this.hasCertificateValidationErrors();
  }

  /**
   * Retrieves the 'lfdCertificates' form array from the certificate form.
   *
   * @return {UntypedFormArray} The 'lfdCertificates' form array.
   */
  public get lfdCertificates(): UntypedFormArray {
    return this.certificateForm?.get('lfdCertificates') as UntypedFormArray;
  }

  /**
   * Adds a new certificate entry to the lfdCertificates array by creating a new certificate group
   * and appending it to the array.
   */
  public addCertificateEntry(): void {
    this.lfdCertificates?.push(this.createCertificateGroup());
  }

  /**
   * Removes a certificate entry at the specified index from the certificate list.
   * If the certificate list has only one entry, it resets the entire certificate list by adding a new empty entry.
   *
   * @param {number} index - The index of the certificate entry to be removed.
   */
  public removeCertificateEntry(index: number): void {
    if (!this.lfdCertificates) {
      return;
    }
    if (this.lfdCertificates.length === 1) {
      this.resetCertificateEntries();
      return;
    }
    this.lfdCertificates.removeAt(index);
  }

  /**
   * Handles the form submission process, including validation, preparation of necessary documents,
   * and progressing through different submission steps based on the current state.
   */
  public onSubmit(): void {
    const selectedProducts = this.getSelectedProductKeys();
    if (!selectedProducts.length || !this.validateAdditionalSelections()) {
      this.invalidateForm();
      return;
    }

    this.pendingDocuments = this.buildGrundakteDocuments(selectedProducts);
    this.showInvalidInputs = false;

    if (!this.hasGrundeStep) {
      this.finishCombinedSubmit();
      return;
    }

    this.step = 2;
    this.modalRef?.scrollToTop();
  }

  /**
   * Handles the back button click event, which navigates back to the previous step in the submission process.
   */
  public backToStepOne(): void {
    this.step = 1;
  }

  /**
   * Validates Grunde-related child forms, persists owners and folio numbers
   * into the selected property and finishes the overall submission.
   */
  public onConfirmGrunde(): void {
    const isObjectOwnerFormValid = this.objectOwnerComponent?.isObjectOwnerFormValid();
    const isFolioNumberFormValid = this.folioNumberComponent?.isFolioNumberFormValid();
    if (!isObjectOwnerFormValid || !isFolioNumberFormValid) {
      return;
    }

    if (this.selectedProperty) {
      if (this.objectOwnerComponent) {
        this.selectedProperty.objectOwnerList = this.mapObjectOwners();
      }
      if (this.folioNumberComponent) {
        this.persistFolioData();
      }
    }

    this.finishCombinedSubmit();
  }

  /**
   * Opens a confirmation dialog to confirm the removal of Grundakte documents from the cart.
   * If confirmed, emits a removal request event and closes the modal, if applicable.
   */
  public removeGrundakteDocument(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: 'auto',
      data: {
        title: 'Daten löschen?',
        content: 'Sollen die Grundakten-Dokumente aus dem Warenkorb entfernt werden? Alle Angaben gehen verloren.'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeRequested.emit();
        this.modalRef?.closeModal();
      }
    });
  }

  /**
   * Handles the actions required when a modal is closed.
   * Triggers the reset of any pending state and emits a modalClosed event.
   */
  public onModalClosed(): void{
    this.resetPendingState();
    this.modalClosed.emit();
  }

  /**
   * Normalizes user input so the field contains only whole positive numbers.
   *
   * This method:
   *  - removes all non-digit characters (e.g., commas, dots, letters),
   *  - updates the actual input value accordingly,
   *  - triggers Angular change detection so the ngModel receives the cleaned value.
   *
   * Used for fields where only integer values are allowed
   */
  public onIntegerInput(event: any): void {
    const input = event.target as HTMLInputElement;

    // Remove everything except digits
    input.value = input.value.replace(/\D+/g, '');

    // Trigger Angular change detection
    event.target.dispatchEvent(new Event('input'));
  }

  /**
   * Initializes the dynamic selection controls for 'Grundakte' products.
   *
   * This method rebuilds the `grundakteSelections` FormGroup based on the
   * currently available `grundakteProducts`.
   *
   * - Removes all previously registered selection controls to avoid stale entries
   * - Creates one boolean FormControl per product
   * - Ensures product dependent identifier are updated after initializations
   * @private
   */
  private initSelectionControls(): void {
    const selectionGroup = this.grundakteForm?.get('grundakteSelections') as UntypedFormGroup;
    if (!selectionGroup) {
      return;
    }

    // Clear existing controls
    Object.keys(selectionGroup.controls).forEach(control => selectionGroup.removeControl(control));

    // Create one checkbox control per available product
    (this.grundakteProducts || []).forEach(product => {
      selectionGroup.addControl(product.nameShort, new UntypedFormControl(false));
    });

    this.updateProductIdentifiers();
  }

  /**
   * Initializes the Document class for 'bewil' document.
   * Either tries to retrieve vom the session storage or fetches the document class from the server.
   */
  private initBewilDocumentClass(): void {
    const cached = sessionStorage.getItem(this.bewiluKey);
    if (cached) {
      this.assignBewilDocumentClass(JSON.parse(cached) as DocumentClass);
      return;
    }

    this.productService.getDocumentClassByShortname(this.bewiluKey).subscribe(documentClass => {
      this.assignBewilDocumentClass(documentClass);
      sessionStorage.setItem(this.bewiluKey, JSON.stringify(documentClass));
    });
  }

  /**
   * Assigns the specified document class to the `bewilDocumentClass` property and performs related operations.
   *
   * @param {DocumentClass} documentClass - The document class to be assigned. This parameter is used to update the `bewilDocumentClass` property, filter subclasses, and carry out additional actions.
   */
  private assignBewilDocumentClass(documentClass: DocumentClass): void {
    this.bewilDocumentClass = documentClass;
    this.bewilSubClasses = documentClass?.subClasses
        ?.filter(sub => !this.normalize(sub.className).includes('bewilu')) || [];
    this.determineLfdSubClassKey();
    this.addBewilSubClassControls();
    this.updateValuesForExistingGrundakteDocuments();
  }

  /**
   * Determines the correct subclass for 'Bewilligungsurkunden zu ausgewählten Rechten' and assigns it to the `lfdSubClassKey` property.
   */
  private determineLfdSubClassKey(): void {
    const lfdSub = this.bewilSubClasses.find(sub => this.isLfdSubClass(sub));
    this.lfdSubClassKey = lfdSub?.className;
  }

  /**
   * Determines whether a specific sub class can be associated with 'Bewilligungsurkunden zu ausgewählten Rechten'
   *
   * @param subClass Sub class to check
   * @return true if the given subClass can be associated with 'Bewilligungsurkunden zu ausgewählten Rechten' else false
   */
  private isLfdSubClass(subClass: SubClass): boolean {
    const combined = `${subClass?.className ?? ''} ${subClass?.name ?? ''}`.toLowerCase();
    return combined.includes('bewunr');
  }

  /**
   * Adds controls for "bewilSubclasses" dynamically to the form.
   * Removes existing controls in the "bewilSubclasses" form group and adds new ones based on the `bewilSubClasses` data.
   * Also registers subscriptions for these new controls.
   */
  private addBewilSubClassControls(): void {
    const subGroup = this.grundakteForm?.get('bewilSubclasses') as UntypedFormGroup;
    if (!subGroup) {
      return;
    }
    Object.keys(subGroup.controls).forEach(control => subGroup.removeControl(control));
    this.bewilSubClasses.forEach(subClass => {
      subGroup.addControl(subClass.className, new UntypedFormControl(false));
    });
    this.registerSubClassSubscriptions();
  }


  /**
   * Helper function that resolves and stores short-names for specific 'Grundakte' products.
   * Scans the provided 'grundakteProducts' list and derives the relevant product short-names.
   *
   * Currently handles:
   * - 'bewilShortName': product whose name contains 'bewil'
   * - 'teilungShortName': product whose name contains 'teil'
   */
  private updateProductIdentifiers(): void {
    (this.grundakteProducts || []).forEach(product => {
      const normalized = this.normalize(product?.nameShort);
      if (normalized.includes('bewil')) {
        this.bewilShortName = product.nameShort;
      }
      if (normalized.includes('teil')) {
        this.teilungShortName = product.nameShort;
      }
    });
  }

  /**
   * Checks if the provided certificate data is valid and returns a boolean value indicating the result.
   * @return True if the provided certificate data is valid, else false
   */
  private validateAdditionalSelections(): boolean {
    let isValid = true;
    if (this.hasBewilSelected() && this.selectedBewilSubclasses().length === 0) {
      isValid = false;
    }
    if (this.isLfdSubClassSelected()) {
      this.lfdCertificates?.markAllAsTouched();
      if (this.hasCertificateValidationErrors()) {
        isValid = false;
      }
    }
    return isValid;
  }

  /**
   * Build all relevant Document objects based on the given list of productKeys
   *
   * @param productKeys Array of product names used to build documents
   * @return Array of all created Document objects
   */
  private buildGrundakteDocuments(productKeys: string[]): Document[] {
    return productKeys.map(key => {
      if (this.isBewilProductKey(key)) {
        return this.buildBewilligungsurkundeDocument(key);
      }
      return this.buildTeilungDocument(key);
    });
  }

  /**
   * Helper function building a document with the provided key as name
   *
   * @param key Name that should be set for the document
   * @return Document with the provided key
   */
  private buildTeilungDocument(key: string): Document {
    const doc = new Document(key);
    doc.information = null;
    return doc;
  }

  /**
   * Helper function build a document with the provided key as name.
   * Adds all selected bewil subclasses as subInformation to the document.
   * Adds the given certificates to the additionalCertificateData of the document.
   *
   * @param key Name that should be set for the document
   * @return Document with the provided key and all selected bewil subclasses and certificates
   */
  private buildBewilligungsurkundeDocument(key: string): Document {
    const doc = new Document(key);
    doc.information = null;
    if (this.bewilDocumentClass) {
      doc.documentClass = this.bewilDocumentClass;
    }

    const selectedSubclasses = this.selectedBewilSubclasses();
    selectedSubclasses.forEach(subKey => {
      const subClass = this.bewilSubClasses.find(sub => sub.className === subKey);
      if (!subClass) {
        return;
      }
      const subInformation = new SubInformation();
      subInformation.subClass = subClass;
      subInformation.nameShort = subClass.className;
      subInformation.nameLong = subClass.name;
      subInformation.received = false;
      subInformation.requested = false;
      doc.subInformations.push(subInformation);
    });

    if (this.isLfdSubClassSelected()) {
      const certificates = this.collectCertificateData();
      if (certificates.length) {
        const additionalCertificateData = new AdditionalCertificateData();
        additionalCertificateData.certificates = certificates;
        doc.additionalCertificateData = additionalCertificateData;
      }
    }

    return doc;
  }

  /**
   * Maps raw certificate data into a CertificateData object, normalizing the provided values.
   *
   * @param {any} value - The raw data object containing certificate details such as landRegisterPage, department, serialNumber, and certificate.
   * @return {CertificateData | null} A CertificateData object containing the normalized values, or null if the input data is insufficient or invalid.
   */
  private mapCertificateData(value: any): CertificateData | null {
    const landRegisterPage = this.normalizeCertificateText(value?.landRegisterPage);
    const department = this.normalizeCertificateText(value?.department);
    const serialNumber = this.normalizeCertificateText(value?.serialNumber);
    const certificateText = this.normalizeCertificateText(value?.certificate);

    if (!landRegisterPage && !department && !serialNumber && !certificateText) {
      return null;
    }

    if (!landRegisterPage || !department || !serialNumber || !certificateText) {
      return null;
    }

    const certificate = new CertificateData();
    certificate.landRegisterPage = landRegisterPage;
    certificate.department = department;
    certificate.serialNumber = serialNumber;
    certificate.certificate = certificateText;
    return certificate;
  }

  /**
   * Determines whether the 'bewil' selection is present and selected within the form group.
   *
   * @return {boolean} True if the 'bewil' selection is selected; otherwise, false.
   */
  private hasBewilSelected(): boolean {
    if (!this.bewilShortName) {
      return false;
    }
    const group = this.grundakteForm.get('grundakteSelections') as UntypedFormGroup;
    return !!group?.get(this.bewilShortName)?.value;
  }

  /**
   * Determines all selected bewil subclasses within the form group.
   * @return Array of selected bewil subclasses
   */
  private selectedBewilSubclasses(): string[] {
    return this.extractCheckedKeys('bewilSubclasses');
  }

  /**
   * Retrieves the selected product keys from the form group.
   *
   * @return {string[]} An array of selected product keys.
   */
  private getSelectedProductKeys(): string[] {
    return this.extractCheckedKeys('grundakteSelections');
  }

  /**
   * Extracts the keys from a form group where the corresponding values are truthy.
   *
   * @param controlName The name of the form control to retrieve and evaluate keys from.
   * @return An array of keys whose associated values are truthy in the specified form group.
   */
  private extractCheckedKeys(controlName: string): string[] {
    const group = this.grundakteForm.get(controlName) as UntypedFormGroup;
    return Object.entries(group?.value || {})
      .filter(([_, checked]) => !!checked)
      .map(([key]) => key);
  }

  /**
   * Creates and returns a new certificate group as an instance of UntypedFormGroup.
   * This method initializes the form group by delegating to the createCertificateGroupWithValues method.
   *
   * @return {UntypedFormGroup} The initialized certificate group form object.
   */
  private createCertificateGroup(): UntypedFormGroup {
    return this.createCertificateGroupWithValues();
  }

  /**
   * Marks alls invalid inputs and scrolls the current modal to the top of the viewport.
   */
  private invalidateForm(): void {
    this.showInvalidInputs = true;
    if (this.grundakteForm) {
      FormUtils.markInvalidInputs(this.grundakteForm);
    }
    if (this.certificateForm) {
      FormUtils.markInvalidInputs(this.certificateForm);
    }
    this.modalRef?.scrollToTop();
  }

  /**
   * Emits all pending documents (including optional Grunde), resets temporary
   * state and closes the modal.
   */
  private finishCombinedSubmit(): void {
    const docs: Document[] = [];
    docs.push(...(this.pendingDocuments || []));
    if (this.grundeShortName && !this.hasExistingGrunde && this.shouldAddGrundeForSelection()) {
      docs.push(new Document(this.grundeShortName));
    }

    this.submitted.emit(docs);
    this.resetPendingState();
    this.modalRef?.closeModal();
  }

  /**
   * Resets the pending state of the modal, including the list of pending documents and the current step.
   */
  private resetPendingState(): void {
    this.pendingDocuments = [];
    this.step = 1;
  }

  /**
   * Maps object owner form values into ObjectOwner DTOs.
   *
   * @return {ObjectOwner[]} The mapped list of object owners.
   */
  private mapObjectOwners(): ObjectOwner[] {
    return this.objectOwnerComponent?.objectOwners.controls.map(group => {
      const value = group.value;
      return {
        firstname: value.firstname?.trim() ?? '',
        lastname: value.lastname?.trim() ?? '',
        birthDate: value.birthDate ?? null,
        birthName: value.birthName?.trim() ?? '',
        company: value.company?.trim() ?? '',
        objectOwnerType: value.objectOwnerType
      } as ObjectOwner;
    }) ?? [];
  }

  /**
   * Persists folio number data into the selected property,
   * including folio creation/assignment and research flag.
   */
  private persistFolioData(): void {
    const formValues = this.folioNumberComponent?.folioNumberForm.get('numbers')?.value ?? [];

    try {
      LandregisterfolioUtils.createFoliosIfMissing(this.selectedProperty);
      LandregisterfolioUtils.assignFolioNumbers(this.selectedProperty.landregisterfolios, formValues);
    } catch (error) {
      console.error('Error while assigning folio numbers', error);
    }

    this.selectedProperty.dataResearch =
      this.folioNumberComponent?.folioNumberForm.get('dataResearch')?.value ?? false;
  }

  private get grundeProduct(): Product | undefined {
    return (this.products || []).find(product =>
      ['grunde', 'grundb'].includes(this.normalize(product?.nameShort)));
  }

  /**
   * Retrieves the short name of the grunde product if available.
   *
   * @return {string | undefined} The short name of the grunde product, or undefined if not available.
   */
  private get grundeShortName(): string | undefined {
    return this.grundeProduct?.nameShort;
  }

  /**
   * Checks if the documents list contains a document with the name 'grunde' or 'grundb' (case-insensitive).
   *
   * @return {boolean} True if a document with the name 'grunde' or 'grundb' exists, false otherwise.
   */
  private get hasExistingGrunde(): boolean {
    const names = (this.documents || []).map(document => (document?.docname || '').toLowerCase());
    return names.includes('grunde') || names.includes('grundb');
  }

  /**
   * Adds "grunde" when a Bewilligungsurkunde selection ("bewilu") is present.
   * This also applies when Bewilligungsurkunde and Teilung are selected together.
   */
  private shouldAddGrundeForSelection(): boolean {
    const selectedNames = this.getSelectedGrundakteSelectionNames();

    return selectedNames.some(name => name.includes('bewilu'));
  }

  /**
   * Returns selected product short names from the current form (step 1) or
   * from already-built pending documents (step 2).
   */
  private getSelectedGrundakteSelectionNames(): string[] {
    if (this.pendingDocuments?.length) {
      return this.pendingDocuments.map(document => this.normalize(document?.docname));
    }

    const selectionGroup = this.grundakteForm?.get('grundakteSelections') as UntypedFormGroup;
    return Object.entries(selectionGroup?.value || {})
      .filter(([_, checked]) => !!checked)
      .map(([key]) => this.normalize(key));
  }

  /**
   * Converts the input string to lowercase. If the input value is null or undefined, it defaults to an empty string before conversion.
   *
   * @param {string} value - The string value to be normalized.
   * @return {string} The normalized string in lowercase.
   */
  private normalize(value: string): string {
    return (value || '').toLowerCase();
  }

  private isBewilProductKey(key: string): boolean {
    return this.normalize(key).includes('bewil');
  }

  /**
   * Helper function that determines the priority of a product in the selection list.
   * Used to sort the products.
   *
   * @param product Product to check
   * @return {number} Priority of the product
   */
  private getGrundaktePriority(product: Product): number {
    const shortName = this.normalize(product?.nameShort);
    if (shortName.includes('teil')) {
      return 0;
    }
    if (shortName.includes('bewil')) {
      return 1;
    }
    return 99;
  }

  private hasCertificateValidationErrors(): boolean {
    return !this.hasCompleteCertificateEntry() || this.hasPartiallyFilledCertificateEntry();
  }

  /**
   * Checks if there is at least one complete certificate entry available.
   *
   * @return {boolean} True if there is at least one complete certificate entry, otherwise false.
   */
  private hasCompleteCertificateEntry(): boolean {
    return this.collectCertificateData().length > 0;
  }

  /**
   * Checks if there is at least one certificate entry that is partially filled.
   *
   * A certificate entry is considered partially filled if it contains any certificate value
   * but is not marked as complete.
   *
   * @return {boolean} True if a certificate entry is partially filled, false otherwise.
   */
  private hasPartiallyFilledCertificateEntry(): boolean {
    return this.collectCertificateEntries()
      .some(entry => this.hasAnyCertificateValue(entry) && !this.isCertificateComplete(entry));
  }

  /**
   * Checks if any certificate field within the provided entry contains a value after normalization.
   *
   * @param {any} entry - The object containing potential certificate fields to check.
   * @return {boolean} True if at least one certificate field has a non-empty value, false otherwise.
   */
  private hasAnyCertificateValue(entry: any): boolean {
    return this.certificateFieldKeys
      .some(key => !!this.normalizeCertificateText(entry?.[key]));
  }

  /**
   * Collects and returns an array of certificate entries from the 'lfdCertificates' form control.
   * If the form control value is not an array, it returns an empty array.
   *
   * @return {any[]} An array of certificate entries.
   */
  private collectCertificateEntries(): any[] {
    const array = this.certificateForm?.get('lfdCertificates') as UntypedFormArray;
    return Array.isArray(array?.value) ? array.value : [];
  }

  /**
   * Collects certificate data by processing certificate entries.
   * Maps each entry to certificate data and filters out invalid data.
   *
   * @return {CertificateData[]} An array of valid certificate data.
   */
  private collectCertificateData(): CertificateData[] {
    return this.collectCertificateEntries()
      .map(entry => this.mapCertificateData(entry))
      .filter((certificate): certificate is CertificateData => !!certificate);
  }

  /**
   * Checks if the certificate is complete by verifying the presence of all required fields.
   *
   * @param {any} entry - The certificate entry to be evaluated.
   * @return {boolean} Returns true if all required fields in the certificate are present and normalized, otherwise false.
   */
  private isCertificateComplete(entry: any): boolean {
    return this.certificateFieldKeys
      .every(key => !!this.normalizeCertificateText(entry?.[key]));
  }

  /**
   * Normalizes the input value by converting it to a string, trimming whitespace,
   * and ensuring a non-empty string is returned. If normalization is not possible,
   * null is returned.
   *
   * @param {any} value - The input value to normalize. Can be of any type.
   * @return {string|null} The normalized string, or null if the input is null, undefined, or cannot be converted to a non-empty string.
   */
  private normalizeCertificateText(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const asString = typeof value === 'string'
      ? value
      : typeof value === 'number'
        ? value.toString()
        : null;
    if (asString === null) {
      return null;
    }
    const trimmed = asString.trim();
    return trimmed.length ? trimmed : null;
  }


  /**
   * Registers subscriptions for each control in the "bewilSubclasses" form group,
   * listening to their value changes and handling subclass changes accordingly.
   *
   * This method first clears any existing subclass subscriptions,
   * retrieves the "bewilSubclasses" form group, and iterates over its controls.
   * For each control, it subscribes to its value changes and stores these subscriptions
   * for cleanup or further management.
   */
  private registerSubClassSubscriptions(): void {
    this.clearSubClassSubscriptions();
    const subGroup = this.grundakteForm?.get('bewilSubclasses') as UntypedFormGroup;
    if (!subGroup) {
      return;
    }
    Object.keys(subGroup.controls).forEach(key => {
      const control = subGroup.get(key);
      if (!control) {
        return;
      }
      const subscription = control.valueChanges.subscribe(selected => this.onSubClassChanged(key, selected));
      this.subClassSubscriptions.push(subscription);
    });
  }

  /**
   * Handles changes to the subclass selection, updates form controls, and resets certificate entries if necessary.
   *
   * @param {string} key - The key of the subclass that has been changed.
   * @param {boolean} selected - Indicates whether the subclass has been selected or deselected.
   */
  private onSubClassChanged(key: string, selected: boolean): void {
    const subGroup = this.grundakteForm?.get('bewilSubclasses') as UntypedFormGroup;
    if (!subGroup) {
      return;
    }

    if (this.lfdSubClassKey && key === this.lfdSubClassKey) {
      if (selected) {
        Object.keys(subGroup.controls)
          .filter(other => other !== this.lfdSubClassKey)
          .forEach(other => subGroup.get(other)?.setValue(false, {emitEvent: false}));
      } else {
        this.resetCertificateEntries();
      }
      return;
    }

    if (selected && this.lfdSubClassKey && subGroup.get(this.lfdSubClassKey)?.value) {
      subGroup.get(this.lfdSubClassKey)?.setValue(false, {emitEvent: false});
      this.resetCertificateEntries();
    }
  }

  /**
   * Resets the current list of certificate entries.
   *
   * This method clears all existing certificate entries and initializes the list
   * with a new certificate group.
   */
  private resetCertificateEntries(): void {
    const certificates = this.lfdCertificates;
    if (!certificates) {
      return;
    }
    while (certificates.length) {
      certificates.removeAt(0);
    }
    certificates.push(this.createCertificateGroup());
  }

  /**
   * Clears all subscriptions stored in the subClassSubscriptions array
   * by unsubscribing each subscription and resetting the array.
   */
  private clearSubClassSubscriptions(): void {
    this.subClassSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.subClassSubscriptions = [];
  }

  /**
   * Updates the values of existing Grundakte documents in the form controls.
   * The method resets all relevant control values, processes matching document data,
   * and sets pre-filled values for specific selections if applicable.
   *
   * The process includes:
   * - Resetting all controls in the "grundakteSelections" and "bewilSubclasses" groups.
   * - Filtering documents that match Grundakte products and updating corresponding controls.
   * - Prefilling specific selections if a matching "Bewil" document is found.
   */
  private updateValuesForExistingGrundakteDocuments(): void {
    if (!this.grundakteForm) {
      return;
    }

    const selectionGroup = this.grundakteForm.get('grundakteSelections') as UntypedFormGroup;
    const subGroup = this.grundakteForm.get('bewilSubclasses') as UntypedFormGroup;

    Object.keys(selectionGroup.controls).forEach(key => selectionGroup.get(key)?.setValue(false, {emitEvent: false}));
    Object.keys(subGroup.controls).forEach(key => subGroup.get(key)?.setValue(false, {emitEvent: false}));
    this.resetCertificateEntries();

    const matchingDocs = (this.documents || []).filter(doc => this.isGrundakteProduct(doc?.docname));
    if (!matchingDocs.length) {
      return;
    }

    matchingDocs.forEach(doc => {
      const controlKey = this.findProductKey(doc.docname);
      if (controlKey) {
        selectionGroup.get(controlKey)?.setValue(true, {emitEvent: false});
      }
    });

    const bewilDoc = matchingDocs.find(doc => this.isBewilProductKey(doc.docname));
    if (bewilDoc) {
      this.prefillBewilSelections(bewilDoc);
      return;
    }
  }

  /**
   * Prefills the `bewilSubclasses` form controls and certificate data based on the provided document.
   *
   * @param {Document} doc - The document containing `subInformations` and `additionalCertificateData` which are used to prefill the form controls and certificate array.
   */
  private prefillBewilSelections(doc: Document): void {
    const subGroup = this.grundakteForm.get('bewilSubclasses') as UntypedFormGroup;
    Object.keys(subGroup.controls).forEach(key => subGroup.get(key)?.setValue(false, {emitEvent: false}));

    doc.subInformations?.forEach(subInfo => {
      const key = subInfo?.nameShort || subInfo?.subClass?.className;
      if (key && subGroup.get(key)) {
        subGroup.get(key)?.setValue(true, {emitEvent: false});
      }
    });

    const certificates = doc.additionalCertificateData?.certificates || [];
    const certificateArray = this.lfdCertificates;
    if (!certificateArray) {
      return;
    }
    while (certificateArray.length) {
      certificateArray.removeAt(0);
    }
    if (certificates.length) {
      certificates.forEach(cert => certificateArray.push(this.createCertificateGroupWithValues(cert)));
    } else {
      certificateArray.push(this.createCertificateGroup());
    }
  }

  /**
   * Checks if the given document name corresponds to a Grundakte product.
   *
   * @param {string} docname - The name of the document to be checked.
   * @return {boolean} Returns true if the document name matches a Grundakte product, otherwise false.
   */
  private isGrundakteProduct(docname: string): boolean {
    if (!docname) {
      return false;
    }
    const normalized = this.normalize(docname);
    return (this.grundakteProducts || [])
      .some(product => this.normalize(product?.nameShort) === normalized);
  }

  /**
   * Finds the product key for a given document name by normalizing and matching it against a list of products.
   *
   * @param {string} docname - The name of the document to search for.
   * @return {string | undefined} The matched product key if found, or undefined if no match is found.
   */
  private findProductKey(docname: string): string | undefined {
    if (!docname) {
      return undefined;
    }
    const normalized = this.normalize(docname);
    return this.grundakteProducts?.find(product => this.normalize(product?.nameShort) === normalized)?.nameShort;
  }

  /**
   * Checks if a product is selected based on its short name.
   *
   * @param shortName - The short name of the product to check selection status for.
   * @return True if the product is selected, otherwise false.
   */
  public isProductSelected(shortName: string): boolean {
    const group = this.grundakteForm?.get('grundakteSelections') as UntypedFormGroup;
    return !!group?.get(shortName)?.value;
  }

  /**
   * Creates a certificate group with given values or defaults.
   *
   * @param {CertificateData} [values] - An optional object containing initial values for the form group controls.
   * @return {UntypedFormGroup} A form group containing controls initialized with the provided values or default empty values.
   */
  private createCertificateGroupWithValues(values?: CertificateData): UntypedFormGroup {
    return this.formBuilder.group({
      landRegisterPage: new UntypedFormControl(values?.landRegisterPage || ''),
      department: new UntypedFormControl(values?.department || ''),
      serialNumber: new UntypedFormControl(values?.serialNumber || ''),
      certificate: new UntypedFormControl(values?.certificate || '')
    });
  }
}
